import { Badge, HStack, Text, VStack } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  ExpenseItemsTimeBillingOrderByColumn,
  type ExpenseItemsTimeBillingWhereColumn,
} from "@src/__generated__/graphql";
import { ExpenseStatusBadge } from "@src/components/modules/expenses/listing/table/ExpenseStatusBadge";
import { BudgetItemColumn } from "@src/components/modules/invoices/outgoing/invoice-items/components/columns";
import { PaidAtCell } from "@src/components/modules/invoices/shared/columns";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  Link,
  LoadingOverlay,
  TruncatedTextWithTooltip,
} from "@src/components/ui-kit";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { appStore } from "@src/stores/AppStore";
import { FiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { currency, formatDate } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { useUpdateURI } from "@src/utils/hooks/useUpdateURI";
import mapToOptions from "@src/utils/map-to-options";
import { lowerCase, upperFirst } from "lodash";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useRouter } from "next/router";
import type { Route } from "nextjs-routes";
import { createContext, Fragment, useContext } from "react";
import {
  BrandColumn,
  CostCenterColumn,
  EuroCurrencyStatusColumn,
  LockedTimeBillingColumn,
  ProjectColumn,
  ResponsibleColumn,
  TimeBillingsColumn,
} from "./components/columns";
import { type ExpenseItemsTableItem, StoreExpenseItems } from "./store";

export const ExpenseItemsStoreContext = createContext<StoreExpenseItems>(
  null as unknown as StoreExpenseItems,
);

export function useExpenseItemsStore() {
  return useContext(ExpenseItemsStoreContext);
}

export const ExpenseItemsListing = observer(function ExpenseItemsListing() {
  const { isReady, query } = useRouter();
  const appStore = useStore();
  const { store, columns } = useLocalObservable(() => ({
    store: new StoreExpenseItems(appStore, query),
    columns: getColumns(appStore.workspaceStore.companies.length <= 1),
  }));

  useUpdateURI(store.searchParams, undefined, !isReady);

  return (
    <ExpenseItemsStoreContext.Provider value={store}>
      <Fragment>
        <DataTable.Filter>
          <FiltersPanel<ExpenseItemsTimeBillingWhereColumn>
            filters={store.where}
            searchPlaceholder={t`Search by expense no, contractor, project, budget item`}
            onSearch={store.setSearchTerm}
            searchDefaultValue={store.searchTerm}
          />
        </DataTable.Filter>

        <DataTable.SummaryBox hideBg>
          <DataTable.SummaryItem
            label={t`Count`}
            isLoaded={!store.isSummaryLoading}
          >
            {store.summary?.count ?? 0}
          </DataTable.SummaryItem>

          <DataTable.SummaryItem
            label={t`Total amount`}
            isLoaded={!store.isSummaryLoading}
          >
            {currency.formatByWorkspace(store.summary?.amount ?? 0)}
          </DataTable.SummaryItem>
        </DataTable.SummaryBox>

        <LoadingOverlay isLoading={store.isLoading} position="absolute">
          <DataTable.Body
            tableKey={store.tableKey}
            data={store.tableData}
            columns={columns}
            orderedBy={store.orderBy.clauses[0]}
            onOrderByChange={store.handleOrderChange}
            allIds={store.bulkActions.allIds}
            selected={store.bulkActions.selectedIdsArray}
            onSelect={store.handleSelectionChange}
            bulkActionsInfoBlock={
              <HStack fontSize="xs">
                <VStack align="end" spacing="0">
                  <Text fontSize="inherit" fontWeight="semibold">
                    <Trans>Total amount</Trans>
                  </Text>

                  <Text fontSize="inherit">
                    {currency.formatByWorkspace(store.selectedTotalAmount)}
                  </Text>
                </VStack>
              </HStack>
            }
          />
        </LoadingOverlay>

        <Pagination store={store.pagination} />
      </Fragment>
    </ExpenseItemsStoreContext.Provider>
  );
});

function getColumns(
  hideLegalEntity: boolean,
): BodyColumnDef<
  ExpenseItemsTableItem,
  ExpenseItemsTimeBillingOrderByColumn
>[] {
  return [
    {
      id: "expense_id",
      meta: { label: t`Expense ID` },
      header: t`Expense ID`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.Id,
      cell: ({ row }) => {
        return (
          <HStack>
            <Link
              href={{
                pathname: "/expenses/edit" as Route["pathname"],
                query: { id: row.original.expense.id },
              }}
            >
              {row.original.expense.id}
            </Link>

            <EuroCurrencyStatusColumn
              invoiceStatus={row.original.expense.invoice_status}
            />
          </HStack>
        );
      },
    },
    {
      id: "expense_no",
      meta: { label: t`Expense No` },
      header: t`Expense No`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.ExpenseNo,
      cell: ({ row }) => {
        const value =
          row.original.expense.expense_no ??
          row.original.expense.document_number;

        if (!value) return null;

        return (
          <Link
            href={{
              pathname: "/expenses/edit" as Route["pathname"],
              query: { id: row.original.expense.id },
            }}
            target="_blank"
          >
            {value}
          </Link>
        );
      },
    },
    {
      id: "type",
      meta: { label: t`Type` },
      header: t`Type`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.Type,
      cell: ({ row }) => upperFirst(lowerCase(row.original.expense.type)),
    },
    {
      id: "status",
      meta: { label: t`Status` },
      header: t`Status`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.Status,
      cell: ({ row }) => (
        <ExpenseStatusBadge status={row.original.expense.status} />
      ),
    },
    {
      id: "amount",
      meta: { label: t`Amount`, align: "right" },
      header: t`Amount`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.Amount,
      cell: ({ row }) => currency.formatByWorkspace(row.original.amount),
    },
    {
      id: "responsible",
      meta: { label: t`Responsible` },
      header: t`Responsible`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.Responsible,
      cell: ResponsibleColumn,
    },
    {
      id: "contractor",
      meta: { label: t`Contractor` },
      header: t`Contractor`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.ContractorName,
      cell: ({ row }) => {
        const contractor = row.original.expense.contractor;

        if (!contractor) return null;

        return (
          <Link
            href={{
              pathname: "/contractors/detail" as Route["pathname"],
              query: { id: contractor.id },
            }}
            target="_blank"
          >
            {contractor.legal_name}
          </Link>
        );
      },
    },
    {
      id: "legal_entity",
      meta: { label: t`Legal entity`, defaultHidden: hideLegalEntity },
      header: t`Legal entity`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.LegalEntity,
      enableHiding: !hideLegalEntity,
      cell: ({ row }) => {
        if (!row.original.expense.workspace_company_id) return null;
        const company = appStore.workspaceStore.companies.find(
          (c) => c.id === row.original.expense.workspace_company_id,
        );
        return company?.internal_name;
      },
    },
    {
      id: "brand",
      meta: { label: t`Brand` },
      header: t`Brand`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.Brand,
      cell: ({ row }) => {
        return (
          <TruncatedTextWithTooltip
            tooltip={
              <BrandColumn brandId={row.original.brand_id} color="white" />
            }
            text={<BrandColumn brandId={row.original.brand_id} />}
          />
        );
      },
    },
    {
      id: "project",
      meta: { label: t`Project` },
      header: t`Project`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.Project,
      cell: ({ row }) => {
        const project = row.original.expense.projects.find(
          (project) => project.id === row.original.project_id,
        );

        if (!project) return null;

        return (
          <TruncatedTextWithTooltip
            tooltip={<ProjectColumn project={project} color="white" />}
            text={<ProjectColumn project={project} />}
          />
        );
      },
    },
    {
      id: "budget_item",
      meta: { label: t`Budget item` },
      header: t`Budget item`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.BudgetItem,
      cell: ({ row }) => {
        return (
          <TruncatedTextWithTooltip
            text={
              <BudgetItemColumn
                budgetItem={row.original.budgetItem}
                budgetItemType={row.original.budget_item_type}
              />
            }
          />
        );
      },
    },
    {
      id: "accounting_account",
      meta: { label: t`Accounting account` },
      header: t`Accounting account`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.AccountingAccount,
      cell: ({ row }) =>
        row.original.accountingAccount
          ? mapToOptions.accountingAccounts([row.original.accountingAccount])[0]
              .label
          : null,
    },
    {
      id: "cost_center",
      meta: { label: t`Cost center` },
      header: t`Cost center`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.CostCenter,
      cell: CostCenterColumn,
    },
    {
      id: "description",
      meta: { label: t`Description` },
      header: t`Description`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.Description,
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original.description?.title} />
      ),
    },
    {
      id: "tags",
      meta: { label: t`Tags` },
      header: t`Tags`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.Tags,
      cell: ({ row }) => {
        if (!row.original.tag_ids || row.original.tag_ids.length === 0)
          return null;

        const tags = appStore.tagStore.tags.filter((tag) =>
          row.original.tag_ids?.includes(tag.id),
        );

        return (
          <TruncatedTextWithTooltip
            text={
              <HStack>
                {tags.map((tag) => (
                  <Badge key={tag.id} size="sm">
                    {tag.title}
                  </Badge>
                ))}
              </HStack>
            }
          />
        );
      },
    },
    {
      id: "vat_classification",
      meta: { label: t`VAT classification` },
      header: t`VAT classification`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.VatClassification,
      cell: ({ row }) => {
        if (!row.original.accounting_vat_classification_id) return null;
        return appStore.workspaceStore.accountingVatClassifications.find(
          (v) => v.id === row.original.accounting_vat_classification_id,
        )?.title;
      },
    },
    {
      id: "time_billing",
      meta: { label: t`Time billing` },
      header: t`Time billing`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.TimeBilling,
      cell: ({ row }) => (
        <TimeBillingsColumn
          timeBillings={row.original.timeBillings}
          sourceId={row.original.id}
        />
      ),
    },
    {
      id: "locked_aliquot",
      meta: { label: t`Locked time billing` },
      header: t`Locked time billing`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.LockedTimeBilling,
      cell: ({ row }) => (
        <LockedTimeBillingColumn timeBillings={row.original.timeBillings} />
      ),
    },
    {
      id: "date_of_supply",
      meta: { label: t`Date of supply` },
      header: t`Date of supply`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.DateOfSupply,
      cell: ({ row }) =>
        row.original.expense.date_of_supply
          ? formatDate(new Date(row.original.expense.date_of_supply))
          : null,
    },
    {
      id: "due_date",
      meta: { label: t`Due date` },
      header: t`Due date`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.DueDate,
      cell: ({ row }) =>
        row.original.expense.due_date
          ? formatDate(new Date(row.original.expense.due_date))
          : null,
    },
    {
      id: "paid_at",
      meta: { label: t`Paid at` },
      header: t`Paid at`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.PaidAt,
      cell: ({ row }) => {
        const accountMoves = row.original.expense.accountMoves;
        const paid =
          Array.isArray(accountMoves) && accountMoves.length > 0
            ? accountMoves.reduce(
                (prev, curr) => prev + curr.amount_in_move_currency,
                0,
              )
            : undefined;

        return (
          <PaidAtCell
            paidAt={row.original.expense.paid_at}
            hasPayment={!!paid && paid !== 0}
          />
        );
      },
    },
    {
      id: "payment_method",
      meta: { label: t`Payment method` },
      header: t`Payment method`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.PaymentMethod,
      cell: ({ row }) => row.original.expense.payment_type?.name,
    },
    {
      id: "note",
      meta: { label: t`Note` },
      header: t`Note`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.Note,
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original.expense.note} />
      ),
    },
    {
      id: "internal_note",
      meta: { label: t`Internal note` },
      header: t`Internal note`,
      orderBy: ExpenseItemsTimeBillingOrderByColumn.InternalNote,
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original.expense.internal_note} />
      ),
    },
    {
      id: ACTIONS_COLUMN_ID,
      meta: { label: t`Actions` },
    },
  ];
}
